/**
 * KMS 客户端共享模块
 * 用于创建和管理全局唯一的 KMSAxios 实例（区分 user 和 guest）
 */

import { KMSAxios } from '@isp/kms';
import { v4 as uuidV4 } from "uuid";
import type { AxiosInstance } from 'axios';
import { getKMSConfig, getAppEnv, shouldEncryptUrl } from './kmsConfig';

// 全局 KMSAxios 实例（单例）
let guestKmsAxios: KMSAxios | null = null;
let userKmsAxios: KMSAxios | null = null;

/**
 * 创建 KMSAxios 实例
 * @param role 客户端角色：'guest' 或 'user'
 */
function createKMSAxios(
  role: 'guest' | 'user',
): KMSAxios {
  const config = getKMSConfig();
  
  return new KMSAxios({
    env: getAppEnv() as 'dev' | 'test' | 'prod' | 'priv',
    algorithm: config.algorithm,
    cacheType: 'localStorage',
    debug: config.verbose,
    retries: 1,
    needEncrypt: (url) => {
      // 基础 URL 检查
      if (!url || !shouldEncryptUrl(url)) {
        return false;
      }
      
      // 根据角色细分 URL 过滤逻辑
      if (role === 'user') {
        // user 实例只处理包含 auth/user/ 的请求
        return url.includes('auth/user/');
      } else {
        // guest 实例处理不包含 auth/user/ 的请求
        return !url.includes('auth/user/');
      }
    }
  });
}

/**
 * 获取全局 User KMSAxios 实例
 */
function getUserKMSAxios(): KMSAxios {
  if (!userKmsAxios) {
    userKmsAxios = createKMSAxios('user');
  }
  return userKmsAxios;
}

/**
 * 获取全局 Guest KMSAxios 实例
 */
function getGuestKMSAxios(): KMSAxios {
  if (!guestKmsAxios) {
    guestKmsAxios = createKMSAxios('guest');
    guestKmsAxios.init({
      role: 'guest',
      guestId: uuidV4(),
    });
  }
  return guestKmsAxios;
}

/**
 * 初始化 User KMS 角色信息
 * 当登录成功或 token 刷新时调用
 * @param token 用户 token
 */
export function initUserKMS(token: string) {
  console.log('开始初始化 User KMS 角色信息，token =', token);
  const kms = getUserKMSAxios();
  kms.init({
    role: 'user',
    token: `Bearer ${token}`,
  });
}

/**
 * 注册 KMS 请求拦截器
 * @param axiosInstance axios 实例
 * @param role 客户端角色：'guest' 或 'user'
 * @returns 返回请求拦截器 ID
 */
export function registerKMSRequestInterceptor(
  axiosInstance: AxiosInstance,
  role: 'guest' | 'user'
): number | null {
  try {
    const kmsAxios = role === 'user' ? getUserKMSAxios() : getGuestKMSAxios();
    return kmsAxios.useRequestInterceptors(axiosInstance);
  } catch (error) {
    console.error(`[KMS] 注册 ${role} 请求拦截器失败:`, error);
    return null;
  }
}

/**
 * 注册 KMS 响应拦截器
 * @param axiosInstance axios 实例
 * @param role 客户端角色：'guest' 或 'user'
 * @returns 返回响应拦截器 ID
 */
export function registerKMSResponseInterceptor(
  axiosInstance: AxiosInstance,
  role: 'guest' | 'user',
): number | null {
  try {
    const kmsAxios = role === 'user' ? getUserKMSAxios() : getGuestKMSAxios();
    return kmsAxios.useResponseInterceptors(axiosInstance);
  } catch (error) {
    console.error(`[KMS] 注册 ${role} 响应拦截器失败:`, error);
    return null;
  }
}